import { useCallback, useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  type FirestoreError,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import type { IJob } from "@/hooks/useJobs";
import type { Collaborator } from "@/components/Sharing/types";

interface UseUpdateJobAccessParams {
  jobId: string;
  job: IJob | null;
  manuscriptTitle: string;
  manuscriptAuthor: string;
  fileName: string;
}

interface UseUpdateJobAccessResult {
  collaborators: Collaborator[];
  ownerEmail: string;
  isLoading: boolean;
  accessRevoked: boolean;
  addCollaborators: (emails: string[]) => Promise<void>;
  removeCollaborator: (email: string) => Promise<void>;
}

export function useUpdateJobAccess({
  jobId,
  job,
  manuscriptTitle,
  manuscriptAuthor,
  fileName,
}: UseUpdateJobAccessParams): UseUpdateJobAccessResult {
  const { authUser } = useAuth();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [sharedOwnerEmail, setSharedOwnerEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessRevoked, setAccessRevoked] = useState(false);

  useEffect(() => {
    if (!authUser || !jobId) {
      setCollaborators([]);
      setSharedOwnerEmail(null);
      setIsLoading(false);
      return;
    }

    const shareRef = doc(db, "shared_jobs", jobId);
    const unsubscribe = onSnapshot(
      shareRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const emails: string[] = data.collaborator_emails ?? [];
          setCollaborators(emails.map((email) => ({ email })));
          setSharedOwnerEmail(data.owner_email ?? null);
        } else {
          setCollaborators([]);
          setSharedOwnerEmail(null);
        }
        setIsLoading(false);
      },
      (err) => {
        if ((err as FirestoreError).code === "permission-denied") {
          setAccessRevoked(true);
        } else {
          logError(err as Error);
        }
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, jobId]);

  const ownerEmail = sharedOwnerEmail ?? (authUser?.email ?? "").toLowerCase();

  const addCollaborators = useCallback(
    async (emails: string[]) => {
      if (!authUser || !jobId || emails.length === 0) return;
      if (!job || !job.tenantId || !job.summaryId) {
        const err = new Error(
          "useUpdateJobAccess: missing job data (tenantId/summaryId); cannot share",
        );
        logError(err);
        throw err;
      }

      const lowered = emails.map((email) => email.toLowerCase());
      const shareRef = doc(db, "shared_jobs", jobId);

      try {
        const existing = await getDoc(shareRef);
        if (existing.exists()) {
          await updateDoc(shareRef, {
            collaborator_emails: arrayUnion(...lowered),
            updated_at: serverTimestamp(),
          });
        } else {
          await setDoc(shareRef, {
            job_id: jobId,
            summary_id: job.summaryId,
            owner_id: authUser.uid,
            owner_email: (authUser.email ?? "").toLowerCase(),
            tenant_id: job.tenantId,
            manuscript_title: manuscriptTitle,
            manuscript_author: manuscriptAuthor,
            file_name: fileName,
            desired_word_count: job.desiredWordCount,
            collaborator_emails: lowered,
            has_opened_emails: [],
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });
        }
      } catch (err) {
        logError(err as Error);
        throw err;
      }
    },
    [authUser, jobId, job, manuscriptTitle, manuscriptAuthor, fileName],
  );

  const removeCollaborator = useCallback(
    async (email: string) => {
      if (!authUser || !jobId || !email) return;

      const lowered = email.toLowerCase();
      const shareRef = doc(db, "shared_jobs", jobId);

      try {
        await updateDoc(shareRef, {
          collaborator_emails: arrayRemove(lowered),
          has_opened_emails: arrayRemove(lowered),
          updated_at: serverTimestamp(),
        });
      } catch (err) {
        logError(err as Error);
        throw err;
      }
    },
    [authUser, jobId],
  );

  return {
    collaborators,
    ownerEmail,
    isLoading,
    accessRevoked,
    addCollaborators,
    removeCollaborator,
  };
}
